// Funded R&D, patents & trade secrets (DESIGN §13). Runs daily.
import { chance } from '../core/rng.js';
import { dayToDate } from '../core/time.js';
import { TECH, UPGRADES, techCost, techDays, canStart, FUNDING, PATENT_YEARS, patentRoyaltyPerYear } from '../data/tech.js';
import { EQUIPMENT, eqDef, stat, yf, yearOf, eraDollars } from './defs.js';
import { startTimer, cancelTimer } from './timers.js';
import { queueDecision, popDecision } from './security.js';
import { clamp, notify, log, spend, fmt$ } from './util.js';
import { grant } from '../economy/tokens.js';

export { TECH, UPGRADES };

export function newResearch() {
  return { points: 0, labs: [], projects: {}, done: {}, licenses: [], unlockedDefs: {}, upgrades: {}, extraSlots: 0 };
}

// ---- effects of upgrades (research or historical year reached) ----------------
const UPGRADE_TECH = {};
for (const t of Object.values(TECH)) for (const u of t.unlocks ?? []) UPGRADE_TECH[u] ??= t;

export function hasUpgrade(state, id) {
  if (state.research?.upgrades?.[id]) return true;
  const t = UPGRADE_TECH[id];
  return !!t && yf(state) >= t.historicalYear;
}

// Number of upgrades whose historical year has been reached at fractional year y (sorted thresholds, binary search).
const HIST_YEARS = Object.keys(UPGRADES).map((id) => UPGRADE_TECH[id]?.historicalYear).filter((y) => y != null).sort((a, b) => a - b);
function historicalUpgrades(y) {
  let lo = 0, hi = HIST_YEARS.length;
  while (lo < hi) { const m = (lo + hi) >> 1; if (HIST_YEARS[m] <= y) lo = m + 1; else hi = m; }
  return lo;
}
// Cached per state per day (upgrades only change on research completion / new year).
const effCache = new WeakMap();
export function effects(state) {
  const c = effCache.get(state);
  const nDone = Object.keys(state.research?.upgrades ?? {}).length;
  if (c && c.day === state.day && c.n === nDone) return c.e;
  // perf: the active set only changes when research lands (nDone) or a historical year is crossed (hk) — reuse across days
  const hk = historicalUpgrades(yf(state));
  if (c && c.hk === hk && c.n === nDone && c.ups === state.research?.upgrades) { c.day = state.day; return c.e; }
  const e = computeEffects(state);
  effCache.set(state, { day: state.day, n: nDone, hk, ups: state.research?.upgrades, e });
  return e;
}

function computeEffects(state) {
  const e = { bitLifeMult: 1, wobWearExpDelta: 0, fishingTimeMult: 1, highWobRiskMult: 1, geoSigmaDelta: 0, incidentMult: 1, guardFactorAdd: 0, ropMult: 1, rfAdd: 0, showFounder: false, eLogs: false };
  for (const [id, u] of Object.entries(UPGRADES)) {
    if (!hasUpgrade(state, id)) continue;
    for (const [k, v] of Object.entries(u.effects ?? {})) {
      if (typeof v === 'boolean') e[k] = e[k] || v;
      else if (v && typeof v === 'object') {
        // object-valued effects (e.g. mitigates: { well_fire: 0.6 }): merge per key; fractions stack as 1 − Π(1 − f)
        const o = (e[k] && typeof e[k] === 'object') ? e[k] : (e[k] = {});
        for (const [sk, sv] of Object.entries(v)) o[sk] = typeof sv === 'number' ? 1 - (1 - (o[sk] ?? 0)) * (1 - sv) : sv;
      } else if (k.endsWith('Mult')) e[k] = (e[k] ?? 1) * v;
      else e[k] = (e[k] ?? 0) + v;
    }
  }
  return e;
}

// Shop gate: an item is available when its historical year arrives OR the player researched it.
export function isUnlocked(state, defId) {
  const def = EQUIPMENT[defId];
  if (!def) return false;
  return yf(state) >= def.year || !!state.research?.unlockedDefs?.[defId];
}

// ---- labs & staff ----------------------------------------------------------------
export function labs(state) {
  return Object.values(state.items).filter((i) => eqDef(i.defId)?.cat === 'building');
}

export function labSlots(state) {
  return labs(state).reduce((a, l) => a + stat(eqDef(l.defId), 'slots', 1), 0) + (state.research.extraSlots ?? 0);
}

function labTimeMult(state) {
  const ls = labs(state);
  if (!ls.length) return 1;
  return Math.min(...ls.map((l) => stat(eqDef(l.defId), 'timeMult', 1)));
}

export function inventors(state) {
  return Object.values(state.crew).filter((c) => c.role === 'inventor' && c.status === 'active' && !c.injuredUntil && c.labUid && state.items[c.labUid]);
}

function speed(state, proj, ctx = null) {
  const f = FUNDING[proj.funding] ?? FUNDING.standard;
  const n = ctx ? ctx.n : Math.min(5, inventors(state).length);
  return (1 / f.timeMult) / (ctx ? ctx.ltm : labTimeMult(state)) / (1 - 0.08 * n);
}
// researchView quotes ~100 techs × up to 13 start years: the lab/inventor terms are computed once (perf).
const speedCtx = (state) => ({ n: Math.min(5, inventors(state).length), ltm: labTimeMult(state) });

function fieldExperience(state, tech) {
  const st = state.company.stats;
  if (['bits', 'string', 'rigs', 'well_control', 'mud'].includes(tech.category)) return Math.min(0.2, (st.ftDrilled / 1000) * 0.01);
  if (['production', 'survey', 'transport'].includes(tech.category)) return Math.min(0.2, (st.wellsDrilled ?? 0) * 0.01);
  return 0;
}

// Quote cost/days for starting `techId` now (or at fractional year y).
export function quote(state, techId, funding = 'standard', y = yf(state), ctx = null) {
  const t = TECH[techId];
  const f = FUNDING[funding] ?? FUNDING.standard;
  const disc = fieldExperience(state, t);
  const cost = techCost(t, y) * (1 - disc);
  const stdDays = techDays(t, y);
  const proj = { funding };
  const days = Math.ceil(stdDays / speed(state, proj, ctx));
  const perDay = (cost / stdDays) * f.spendMult;
  return { cost, stdDays, days, perDay, total: perDay * days, discount: disc, yearsEarly: Math.max(0, t.historicalYear - y) };
}

function prereqsMet(state, t) {
  return (t.prereqs ?? []).every((p) => state.research.done[p] || yf(state) >= (TECH[p]?.historicalYear ?? 0));
}

// ---- actions -------------------------------------------------------------------
export function startResearchImpl(state, techId, funding = 'standard') {
  const t = TECH[techId];
  if (!t) return { ok: false, msg: 'Unknown technology.' };
  if (state.research.done[techId]) return { ok: false, msg: 'Already invented.' };
  if (state.research.projects[techId]) return { ok: false, msg: 'Already under way.' };
  if (!FUNDING[funding]) return { ok: false, msg: 'Funding must be shoestring, standard or crash.' };
  if (!canStart(t, yf(state))) return { ok: false, msg: `Nobody could invent that before ${t.minYear}.` };
  if (!prereqsMet(state, t)) return { ok: false, msg: `Needs ${t.prereqs.map((p) => TECH[p]?.name ?? p).join(', ')} first.` };
  if (Object.keys(state.research.projects).length >= labSlots(state)) return { ok: false, msg: labSlots(state) ? 'All lab slots are busy.' : "Build a Tinkerer's Shed first." };
  const q = quote(state, techId, funding);
  const proj = { techId, funding, progressDays: 0, totalDays: q.stdDays, stdDays: q.stdDays, cost: q.cost, spent: 0, startDay: state.day, timerId: null, staff: [], stalled: false, lastSetback: null };
  const tm = startTimer(state, 'RESEARCH', techId, q.days, { techId });
  tm.ref = { type: 'research', id: techId };
  proj.timerId = tm.id;
  state.research.projects[techId] = proj;
  return { ok: true, msg: `Started ${t.name}: ~${fmt$(q.total)} over ~${q.days} days.` };
}

export function setFundingImpl(state, techId, funding) {
  const p = state.research.projects[techId];
  if (!p) return { ok: false, msg: 'No such project.' };
  if (!FUNDING[funding]) return { ok: false, msg: 'Funding must be shoestring, standard or crash.' };
  p.funding = funding;
  return { ok: true, msg: `${TECH[techId].name} now on ${funding} funding.` };
}

export function cancelResearchImpl(state, techId) {
  const p = state.research.projects[techId];
  if (!p) return { ok: false, msg: 'No such project.' };
  cancelTimer(state, p.timerId);
  delete state.research.projects[techId];
  return { ok: true, msg: `${TECH[techId].name} cancelled (${fmt$(p.spent)} sunk).` };
}

export function assignInventorImpl(state, crewId, labUid) {
  const c = state.crew[crewId];
  if (!c || c.status !== 'active') return { ok: false, msg: 'No such crew member.' };
  if (c.role !== 'inventor') return { ok: false, msg: `${c.name} isn't an inventor.` };
  if (labUid && !(state.items[labUid] && eqDef(state.items[labUid].defId)?.cat === 'building')) return { ok: false, msg: 'No such lab.' };
  if (c.rigId && state.rigs[c.rigId]) state.rigs[c.rigId].crewIds = state.rigs[c.rigId].crewIds.filter((x) => x !== c.id);
  c.rigId = null;
  c.labUid = labUid ?? null;
  return { ok: true, msg: labUid ? `${c.name} is at the workbench.` : `${c.name} left the lab.` };
}

export function resolveInventionImpl(state, techId, mode) {
  const d = state.research.done[techId];
  if (!d || d.mode !== 'pending') return { ok: false, msg: 'Nothing to decide for that invention.' };
  if (!['patent', 'secret'].includes(mode)) return { ok: false, msg: "Choose 'patent' or 'secret'." };
  popDecision(state, `inv_${techId}`);
  const t = TECH[techId];
  if (mode === 'patent') {
    if (!d.patentable) return { ok: false, msg: 'Prior art: this was already invented elsewhere, it cannot be patented.' };
    const fee = patentFee(state, t);
    if (!spend(state, fee, 'other')) return { ok: false, msg: `Patent fee is ${fmt$(fee)}.` };
    d.mode = 'patent';
    d.patentExpires = state.day + PATENT_YEARS * 365;
    grant(state, 5, 'patent', techId);
    return { ok: true, msg: `Patent granted on the ${t.name}. Royalties will roll in for ${PATENT_YEARS} years.` };
  }
  d.mode = 'secret';
  return { ok: true, msg: `${t.name} kept as a trade secret.` };
}


export const patentFee = (state, t) => Math.max(eraDollars(state, 35, 1860), techCost(t, yf(state)) * 0.03);

// Token sinks
export function eurekaImpl(state, techId) {
  const p = state.research.projects[techId];
  if (!p) return { ok: false, msg: 'No such project.' };
  p.progressDays += 0.25 * Math.max(0, p.totalDays - p.progressDays);
  return { ok: true, msg: `Eureka! ${TECH[techId].name} leaps ahead.` };
}

export function rerollSetbackImpl(state, techId) {
  const p = state.research.projects[techId];
  if (!p?.lastSetback || state.day - p.lastSetback.day > 30) return { ok: false, msg: 'No recent setback to undo.' };
  p.totalDays -= p.lastSetback.addedDays;
  p.lastSetback = null;
  return { ok: true, msg: 'Setback undone — the prototype works after all.' };
}

// ---- daily ---------------------------------------------------------------------
export function tickResearch(state) {
  const { dom } = dayToDate(state.day);
  for (const p of Object.values(state.research.projects)) {
    const t = TECH[p.techId];
    const f = FUNDING[p.funding] ?? FUNDING.standard;
    const perDay = (p.cost / (p.stdDays ?? techDays(t, yf(state)))) * f.spendMult;
    p.perDay = perDay;
    p.staff = inventors(state).map((c) => c.id);
    if (state.company.cash < perDay) {
      if (!p.stalled) notify(state, 'news', { kind: 'research', text: `${t.name} stalled: not enough cash to pay the inventors.`, level: 'warn' });
      p.stalled = true;
    } else {
      p.stalled = false;
      spend(state, perDay, 'research', false);
      p.spent += perDay;
      p.progressDays += speed(state, p);
    }
    // monthly breakthrough / setback
    if (dom === 1 && state.day !== p.startDay) {
      const genius = inventors(state).filter((c) => c.traits?.includes('genius')).length;
      if (chance(state, 0.03 + 0.02 * genius)) {
        p.progressDays += 0.25 * Math.max(0, p.totalDays - p.progressDays);
        log(state, `Breakthrough on the ${t.name}!`, 'good');
      } else if (chance(state, 0.04 * f.setbackMult)) {
        const added = 0.2 * Math.max(0, p.totalDays - p.progressDays);
        p.totalDays += added;
        p.lastSetback = { day: state.day, addedDays: added };
        notify(state, 'news', { kind: 'research', text: `Setback on the ${t.name}: the prototype failed. (+${Math.round(added)} days)`, level: 'warn' });
      }
    }
    const tm = state.timers[p.timerId];
    const rate = speed(state, p);
    if (tm) tm.daysLeft = Math.max(1, Math.ceil((p.totalDays - p.progressDays) / Math.max(rate, 1e-6)));
    if (p.progressDays >= p.totalDays - 1e-9) completeProject(state, p.techId, false);
  }
  // trade secrets leak (≈10%/yr), checked monthly
  if (dom === 1) {
    for (const [id, d] of Object.entries(state.research.done)) {
      if (d.mode === 'secret' && chance(state, 0.0087)) {
        d.mode = 'leaked';
        notify(state, 'news', { kind: 'research', text: `Rivals reverse-engineered your ${TECH[id]?.name ?? id}. It's in every supply house now.` });
      }
    }
  }
}

export function onResearchTimerDone(state, t, rushed) {
  if (state.research.projects[t.data.techId]) completeProject(state, t.data.techId, rushed);
}

function completeProject(state, techId, rushed) {
  const p = state.research.projects[techId];
  const t = TECH[techId];
  if (!p || !t) return;
  if (p.timerId) cancelTimer(state, p.timerId);
  delete state.research.projects[techId];
  const patentable = yf(state) < t.historicalYear;
  state.research.done[techId] = { day: state.day, mode: 'pending', patentExpires: null, inventedYear: yf(state), patentable };
  for (const u of t.unlocks ?? []) {
    if (EQUIPMENT[u]) state.research.unlockedDefs[u] = true;
    if (UPGRADES[u]) state.research.upgrades[u] = true;
  }
  if (!patentable) { // prior art: nothing to decide — yours to use, not to patent (no card, no pause)
    state.research.done[techId].mode = 'secret';
    notify(state, 'invention', { techId, known: true, level: 'info', text: `Your workshop has perfected the ${t.name}${rushed ? ' (rushed)' : ''}. It was already known elsewhere: yours to use, but not to patent.` });
    return;
  }
  const fee = patentFee(state, t);
  queueDecision(state, {
    kind: 'invention', id: `inv_${techId}`, techId, day: state.day, title: `Invention: ${t.name}`,
    text: `Your workshop has perfected the ${t.name}${rushed ? ' (rushed)' : ''}. ${patentable ? `Patent it (${fmt$(fee)}) to collect royalties from the industry, or keep it secret?` : 'It was already known elsewhere, so it cannot be patented.'}`,
    options: [
      ...(patentable ? [{ choice: 'patent', label: `Patent (${fmt$(fee)})`, cost: fee }] : []),
      { choice: 'secret', label: 'Trade secret' },
    ],
  });
  notify(state, 'invention', { techId, pause: true, level: 'major', text: `Eureka! The ${t.name} is ready.` });
}

// Monthly patent royalty income (era $) — called by finance at month close.
export function patentIncome(state) {
  let inc = 0;
  const y = yf(state);
  for (const [id, d] of Object.entries(state.research.done)) {
    if (d.mode !== 'patent' || (d.patentExpires && state.day > d.patentExpires)) continue;
    // royaltyMult: M2 licensing (licensing infringers raises it, sim/m2/licensing.js)
    inc += patentRoyaltyPerYear(TECH[id], y, d.inventedYear) / 12 * (d.royaltyMult ?? 1);
  }
  return inc;
}

export function playerHoldsPatent(state, techId) {
  const d = state.research?.done?.[techId];
  return d?.mode === 'patent' && (!d.patentExpires || state.day <= d.patentExpires);
}

// ---- view ----------------------------------------------------------------------
export function researchView(state) {
  const y = yf(state);
  const out = { available: [], locked: [], active: [], done: [], slots: labSlots(state), used: Object.keys(state.research.projects).length, labs: labs(state).map((l) => ({ uid: l.uid, defId: l.defId, name: eqDef(l.defId)?.name })), inventors: inventors(state).map((c) => c.id), patentIncomeMonthly: patentIncome(state) };
  const ctx = speedCtx(state);
  for (const t of Object.values(TECH)) {
    const d = state.research.done[t.id];
    const p = state.research.projects[t.id];
    const base = { id: t.id, name: t.name, era: t.era, category: t.category, historicalYear: t.historicalYear, minYear: t.minYear, prereqs: t.prereqs, unlocks: t.unlocks, desc: t.desc };
    if (d) { out.done.push({ ...base, ...d }); continue; }
    if (p) {
      const rate = speed(state, p, ctx);
      out.active.push({ ...base, ...p, pct: clamp(p.progressDays / p.totalDays, 0, 1), daysLeft: Math.ceil((p.totalDays - p.progressDays) / Math.max(rate, 1e-6)), perDay: (p.cost / (p.stdDays ?? techDays(t, y))) * (FUNDING[p.funding]?.spendMult ?? 1) });
      continue;
    }
    // quotes for each possible start year from minYear to historical year (and now)
    const years = [];
    for (let yy = Math.max(t.minYear, Math.floor(y)); yy <= Math.max(t.historicalYear, Math.floor(y)); yy++) {
      const q = quote(state, t.id, 'standard', yy, ctx);
      years.push({ year: yy, cost: Math.round(q.cost), days: q.days });
      if (years.length > 12) break;
    }
    const entry = { ...base, quote: canStart(t, y) ? quote(state, t.id, 'standard', yf(state), ctx) : null, byStartYear: years, earliestStart: t.minYear, canStartNow: canStart(t, y) && prereqsMet(state, t), historicallyAvailable: y >= t.historicalYear };
    if (entry.canStartNow) out.available.push(entry); else out.locked.push(entry);
  }
  return out;
}
